import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Html5QrcodeScanner } from "html5-qrcode";
import { apiClient } from "../../../api";
import { toast, ToastContainer } from "react-toastify";
import { Loader2, Camera, CheckCircle2, AlertCircle, QrCode, Smartphone } from "lucide-react";

const MobileScanner = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const [isLinking, setIsLinking] = useState(false);
    const [linkSuccess, setLinkSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [scanAttempt, setScanAttempt] = useState(0);
    const scannerRef = useRef(null);

    useEffect(() => {
        if (!token) {
            setError("Missing linking token. Please use the link sent to your email or phone.");
            return;
        }

        // Initialize the standard scanner UI which is more reliable across devices
        const scanner = new Html5QrcodeScanner("mobile-reader", {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            rememberLastUsedCamera: true,
            supportedScanTypes: [0] // Camera only
        });

        scanner.render(
            (decodedText) => {
                handleScanSuccess(decodedText, scanner);
            },
            (err) => {
                // Silent during normal scanning
            }
        );

        scannerRef.current = scanner;

        return () => {
            if (scannerRef.current) {
                scannerRef.current.clear().catch(console.error);
                scannerRef.current = null;
            }
        };
    }, [token, scanAttempt]);

    const handleScanSuccess = async (decodedText, scanner) => {
        let qrId = decodedText;
        if (decodedText.includes("link.onelyk.com/")) {
            qrId = decodedText.split("link.onelyk.com/")[1].split("?")[0];
        } else if (decodedText.includes("/qr/")) {
            qrId = decodedText.split("/qr/")[1].split("?")[0];
        }

        // Pause scanner UI
        if (scanner) {
            scanner.pause();
        }

        performLinking(qrId);
    };

    const performLinking = async (qrId) => {
        setIsLinking(true);
        try {
            const response = await apiClient.post("/api/qrs/claim-from-token", {
                token,
                qrId
            });

            if (response.status === 200) {
                setLinkSuccess(true);
                toast.success("QR Code linked successfully!");
                if (scannerRef.current) {
                    await scannerRef.current.clear().catch(console.error);
                }
            }
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to link QR code.";
            
            // If there's an error, we clear the scanner to show the error state cleanly
            if (scannerRef.current) {
                await scannerRef.current.clear().catch(console.error);
                scannerRef.current = null;
            }
            
            setError(msg);
            toast.error(msg);
        } finally {
            setIsLinking(false);
        }
    };

    const resetScanner = () => {
        setError(null);
        setLinkSuccess(false);
        setScanAttempt(prev => prev + 1);
    };

    if (!token && !error) return null;

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center p-4 md:p-8">
            <ToastContainer />
            <div className="max-w-md w-full bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-gray-100 flex flex-col">
                <div className="bg-gradient-to-br from-indigo-600 to-violet-700 p-8 text-white text-center">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-md">
                        <QrCode className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-2xl font-bold tracking-tight">QR Linker</h1>
                    <p className="text-indigo-100 mt-2 text-sm font-medium">Link your physical QR codes instantly.</p>
                </div>

                <div className="p-6 flex-1 flex flex-col justify-center min-h-[450px]">
                    {linkSuccess ? (
                        <div className="text-center py-4 animate-in fade-in zoom-in duration-300">
                            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CheckCircle2 className="w-10 h-10 text-green-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Success!</h2>
                            <p className="text-gray-600 mb-8 font-medium">QR code linked successfully.</p>
                            <button
                                onClick={resetScanner}
                                className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl transition-all shadow-xl shadow-indigo-100"
                            >
                                Scan Another
                            </button>
                        </div>
                    ) : error ? (
                        <div className="text-center py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="w-24 h-24 bg-red-50 rounded-[2rem] flex items-center justify-center mx-auto mb-8 border-2 border-red-100 shadow-sm">
                                <AlertCircle className="w-12 h-12 text-red-500" />
                            </div>
                            <h2 className="text-2xl font-black text-gray-900 mb-4">Linking Failed</h2>
                            <div className="bg-red-50/50 rounded-2xl p-4 mb-10 border border-red-100/50">
                                <p className="text-gray-700 font-medium leading-relaxed">{error}</p>
                            </div>
                            <button
                                onClick={resetScanner}
                                className="w-full py-5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl transition-all shadow-xl shadow-indigo-100 active:scale-[0.98]"
                            >
                                Try Another Scan
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div id="mobile-reader" className="w-full rounded-3xl overflow-hidden border-2 border-gray-100 bg-gray-50"></div>

                            {isLinking && (
                                <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
                                    <Loader2 className="w-16 h-16 text-indigo-600 animate-spin mb-4" />
                                    <h2 className="text-xl font-bold text-gray-900">Linking QR...</h2>
                                </div>
                            )}

                            {!isLinking && (
                                <div className="text-center">
                                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Powered by OneLyk</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                #mobile-reader {
                    border: none !important;
                }
                #mobile-reader video {
                    border-radius: 1.5rem !important;
                    object-fit: cover !important;
                }
                #mobile-reader__dashboard_section_swaplink {
                    display: none !important;
                }
                #mobile-reader__camera_selection {
                    padding: 8px 12px !important;
                    border-radius: 12px !important;
                    border: 1px solid #e5e7eb !important;
                    background: #f9fafb !important;
                    font-size: 14px !important;
                    margin-bottom: 12px !important;
                }
                #mobile-reader__camera_permission_button {
                    background-color: #4f46e5 !important;
                    color: white !important;
                    padding: 12px 24px !important;
                    border-radius: 12px !important;
                    font-weight: bold !important;
                    border: none !important;
                    cursor: pointer !important;
                    margin: 20px 0 !important;
                }
                #mobile-reader img[alt="Info icon"], #mobile-reader img[alt="Camera menu icon"] {
                    display: none !important;
                }
            `}} />
        </div>
    );
};

export default MobileScanner;
